// Copyright (c) 2025 UXtechLab.
// Originally created by @taekie. Released under the MIT License. See LICENSE for details.
/**
 * VoronoiBubble
 *
 * Main class for creating voronoi treemap visualizations.
 * Renders hierarchical data as organic, pebble-like cells using
 * D3.js and voronoi treemap algorithms.
 *
 * Features:
 * - Hierarchical voronoi treemap layout
 * - Customizable colors, labels, and sizing
 * - Interactive click and hover events
 * - Pebble-style rounded outlines
 * - Label collision detection and adjustment
 * - Region position control for deterministic layouts
 */

import d3 from './utils/d3-bundle.js';
import { PebbleRenderer } from './PebbleRenderer.js';
import { LabelAdjuster } from './LabelAdjuster.js';
import { nestingForVoronoi } from './nestingForVoronoi.js';
import VoronoiBubbleHelpers from './VoronoiBubbleHelpers.js';
import { getBubbleStyles } from './PopupHelpers.js';

/**
 * VoronoiBubble - Main visualization class
 *
 * @example
 * const bubble = new VoronoiBubble();
 * const svg = bubble.render(data, {
 *   width: 1200,
 *   height: 900,
 *   title: 'My VoronoiBubble',
 *   caption: 'Optional subtitle'
 * });
 * document.body.appendChild(svg);
 */
class VoronoiBubble {
  constructor() {
    this.margin = { top: 50, right: 50, bottom: 50, left: 50 };
    this.svg = null;
    this.data = null;
    this.hierarchy = null;
    this.allNodes = null;
    this._pebbleRenderer = null;
    this._labelAdjuster = null;
  }

  // === Default Color Palette ===
  static get DEFAULT_COLORS() {
    return "#afc7dd,#ffe9a9,#f69f8f,#b4c8af,#e9e4d6,#bed1d8,#f8dba1,#fcbc8b,#d7e0c4,#c5b5a6,#b5ccc1,#e9bfb4,#e9f0f6,#fffefb,#fce0db,#e1e9df,#f1f5f7,#fef8ed,#feeada,#fbfcf9,#e5ded7,#e5edea,#fbf5f3,#96b6d3,#ffdf85,#f3836e,#a0b99a,#ddd4be,#a7c1cb,#f5cf80,#fba868,#c7d4ac,#b7a490,#a0bdb0,#e1a799,#d7e3ee,#fff7e1,#facbc3,#d3dfd0,#fdfcfa,#e1eaed,#fcefd5,#fddcc2,#f0f3e9,#dbd2c8,#d6e3dd,#f6e4df,#dee8f1,#fffaeb,#fbd4cc,#d9e3d6,#e7eef1,#fcf3df,#fee1cc,#f4f7ef,#dfd7ce,#dce7e2,#f8ebe7,#e5edf4,#fffdf5,#fcdcd6,#dfe7dc,#eef3f5,#fdf6e8,#fee7d6,#f9faf6,#e3dcd4,#e2ebe7,#faf1ef,#d0b7ba,#b8cec4,#d2b6b6,#b6bdd6,#d9b8b7,#ded5b6,#bac2d7,#c8d5be,#e3bfb7,#f9dfb3,#eac2b8,#c1d3da,#ddc7c1,#d9e2c7,#cfdad5,#eecdc1,#ccdddf,#c7d7e6,#ded6cf,#e7d1cb,#ced9e5,#eedbc8,#d7e3e2,#e3ead2,#ecdcd2,#d9e0e5,#efe1d2,#ebdad7,#eed6da,#e1e6de,#dde4e8,#eee1d8,#f5e8d7,#f1e6dd,#f5e8de,#f3e7e1,#f5eee1,#f5f2ec".split(
      ","
    );
  }

  // === Built-in Palettes ===
  // 명화의 대표색에서 hue와 상대적 명암·채도 관계를 가져오되, OKLCH에서
  // L 0.76~0.90 / C 0.05~0.12 밴드로 압축해 이 렌더러의 밝기 파생과
  // 맞춘 팔레트들. 지배색 가족과 악센트를 교차 배열해 큰 그룹(크기
  // 내림차순 배정)이 지배색을 받는다.
  static get PALETTES() {
    return {
      // 기본: 107색 파스텔 그라데이션 풀
      pastel: VoronoiBubble.DEFAULT_COLORS,
      // 반 고흐, 별이 빛나는 밤 — 파랑 가족 + 금색 악센트
      starryNight: "#99c3ff,#f1e084,#97b9fb,#fbcf75,#9fcfff,#aacaa0,#b6d7fd,#e0c483,#c4e1fe,#98b2dc".split(","),
      // 모네, 수련 — 초록 가족 + 분홍·보라 악센트
      waterLilies: "#94cfa2,#f3b4e2,#8ec1ff,#c3dfbc,#cab2ff,#9bbba1,#fec1d4,#bcd8f5,#afd7aa,#f0dbb7".split(","),
      // 호쿠사이, 가나가와의 큰 파도 — 쪽빛 + 크림·주황
      wave: "#82c8ff,#f2dbaf,#8abded,#f5d596,#8cceff,#fabe81,#a4d6ff,#deb58d,#b9dff5,#93b5da".split(","),
      // 클림트, 키스 — 금색·황토 지배 + 자두·분홍 대비
      kiss: "#f5d274,#d9c7f0,#bac991,#f8dd7e,#e4b699,#f2cae6,#d8c97b,#d6afd2,#f5c772,#c4ad8e".split(","),
      // 모네, 인상·해돋이 — 슬레이트 블루 + 주황
      sunrise: "#99b3d9,#ffad8d,#aac4ea,#ffc097,#86c0ca,#ffd2a2,#bad4f6,#d6b1d2,#9dd4e0,#f3d6ba".split(","),
    };
  }

  // === Default Options ===
  static get DEFAULT_OPTIONS() {
    return {
      width: 1200,
      height: 900,
      title: "",
      caption: "",
      onClick: () => {},
      onHover: null, // (cell|null) => void — cell = { ...row, depth, event, target }; null on leave
      hoverVisualLimit: 0, // Enable cell hover highlight / label reveal up to this leaf-cell count
      onSubgroupLabelHover: null, // (label|null) => void — hover on depth-2 (subgroup) labels; label = { subgroup, key, depth, event, target } (no original row fields); null on leave. Enables pointer-events on labels (clicks are forwarded to the cell behind).
      colorFunc: null,
      sentiment: null, // 'fieldName' | { field, domain:[lo,hi] } — built-in diverging sentiment colormap
      getCellColors: null, // (cellColors) => void - Callback to receive actual cell colors
      sizeLimit: 1000,
      ratioLimit: 0,
      pieSize: 1,
      fontScale: 1, // extra multiplier on top of the automatic canvas-relative font normalization
      groupLabelScale: 1.1, // depth-1 (group) label multiplier
      subgroupLabelScale: 1.05, // depth-2 (subgroup) label multiplier

      colors: VoronoiBubble.DEFAULT_COLORS,
      colorVariation: "standard", // 'standard'(그룹 내 명도 대비, v1 룩) | 'subtle'(차분·전역 일관) | 'strong'(강한 대비)
      seedRandom: 10,
      showGroupLabel: false,
      showPercent: false,
      underLabel: false,
      positions: null,
      forceNodeFunc: null,
      debug: false,
      pebbleRound: 25,
      pebbleWidth: 5,
      groupColors: [],
      // Custom HTML label renderers — one per depth.
      // (datum, defaultHtml, ctx) => HTML string. ctx.depth is 1 (group) or 2 (subgroup).
      renderGroupLabel: null,    // depth 1 (group)
      renderSubgroupLabel: null, // depth 2 (subgroup)
      adaptiveIterations: true,
      cellImage: null, // (datum) => { url, mode: 'fill'|'fit', opacity: 0~1, colorMode: 'original'|'tint' } | null
      labelMode: 'faded', // 'show' | 'faded' | 'hidden'
      levels: ['group', 'subgroup', 'item'], // field names per depth (0, 1, 2)
      value: 'size' // field name for size weight
    };
  }

  // === v1 → v2 rename table (detection only, no behavioral fallback) ===
  static get V1_RENAMES() {
    return {
      options: {
        maptitle: 'title', mapcaption: 'caption',
        showRegion: 'showGroupLabel', showMetaLabel: 'showGroupLabel',
        showLabel: 'labelMode', // v1 boolean → v2 'show' | 'faded' | 'hidden'
        regionPositions: 'positions', metaLabelPositions: 'positions',
        keyColors: 'groupColors', regionColors: 'groupColors', metaLabelColors: 'groupColors',
        clickFunc: 'onClick', hoverFunc: 'onHover', labelHoverFunc: 'onSubgroupLabelHover',
        renderLabel: 'renderGroupLabel / renderSubgroupLabel',
        regionLabelRenderer: 'renderGroupLabel', metaLabelRenderer: 'renderGroupLabel',
        labelRenderer: 'renderSubgroupLabel', bigClusterLabelRenderer: 'renderSubgroupLabel',
      },
      // label/text는 여분 필드로 흔해 오탐 위험 → 목록에서 제외.
      fields: {
        region: 'group', metaLabel: 'group', bigClusterLabel: 'subgroup',
        clusterLabel: 'item', bubbleSize: 'size', budget: 'size',
      },
    };
  }

  /**
   * Detect v1 option/field names and print a single migration notice per render.
   * Detection only — v1 names are NOT mapped to v2 behavior.
   * @param {Object} options - Raw user options
   * @param {Object} [firstRow] - First data row (field-name probe)
   */
  _warnV1Names(options, firstRow) {
    const { options: optMap, fields: fieldMap } = VoronoiBubble.V1_RENAMES;
    const hits = Object.keys(options)
      .filter((k) => k in optMap)
      .map((k) => `옵션 ${k} → ${optMap[k]}`);
    const defaults = VoronoiBubble.DEFAULT_OPTIONS;
    const usingDefaultLevels = !options.levels ||
      options.levels.every((f, i) => f === defaults.levels[i]);
    const hasV2Field = firstRow &&
      ['group', 'subgroup', 'item'].some((f) => f in firstRow);
    if (firstRow && usingDefaultLevels && !hasV2Field) {
      hits.push(...Object.keys(fieldMap)
        .filter((k) => k in firstRow)
        .map((k) => `필드 ${k} → ${fieldMap[k]}`));
    }
    if (hits.length) {
      console.error(
        `[VoronoiBubble] v1 이름이 감지되었습니다. v2에서는 동작하지 않습니다.\n  ` +
        hits.join('\n  ') +
        `\n마이그레이션 가이드: docs/MIGRATION.md`,
      );
    }
  }

  // === Getter for post-processing modules ===
  get pebbleRenderer() {
    if (!this._pebbleRenderer) {
      this._pebbleRenderer = new PebbleRenderer(d3);
    }
    return this._pebbleRenderer;
  }

  get labelAdjuster() {
    if (!this._labelAdjuster) {
      this._labelAdjuster = new LabelAdjuster(d3);
    }
    return this._labelAdjuster;
  }

  // === Public Methods ===

  /**
   * Render chart - returns SVG element
   * @param {Object[]} data - Data array to visualize
   * @param {Object} [options] - Rendering options
   * @returns {SVGSVGElement} - Generated SVG element
   */
  render(data, options = {}) {
    // `options = {}` only covers undefined — an explicit `null` must not throw.
    options = options || {};
    this._warnV1Names(options, data?.[0]);
    const normalizedOptions = { ...options };

    this._hasExplicitOnClick = typeof normalizedOptions.onClick === "function";
    this._hasExplicitOnHover = typeof normalizedOptions.onHover === "function";
    this.params = { ...VoronoiBubble.DEFAULT_OPTIONS, ...normalizedOptions };

    // colors 옵션은 배열 또는 내장 팔레트 이름(문자열)을 받는다.
    if (typeof this.params.colors === "string") {
      const preset = VoronoiBubble.PALETTES[this.params.colors];
      if (!preset) {
        console.error(
          `[VoronoiBubble] 알 수 없는 팔레트 '${this.params.colors}' — 기본 팔레트를 사용합니다. ` +
          `사용 가능: ${Object.keys(VoronoiBubble.PALETTES).join(", ")}`,
        );
      }
      this.params.colors = preset || VoronoiBubble.DEFAULT_COLORS;
    }

    // Normalize field names: custom levels/value -> standard group/subgroup/item/size
    const levels = this.params.levels;
    const valueField = this.params.value;
    const stdLevels = ['group', 'subgroup', 'item'];
    const customLevels = !levels.every((f, i) => f === stdLevels[i]);
    const customValue = valueField !== 'size';

    this.data = data.map(d => {
      const normalized = { ...d };
      // Custom levels mapping
      if (customLevels) {
        if (levels[0] !== undefined) normalized.group = d[levels[0]];
        if (levels[1] !== undefined) normalized.subgroup = d[levels[1]];
        if (levels[2] !== undefined) normalized.item = d[levels[2]];
      }
      if (customValue) normalized.size = d[valueField];
      return normalized;
    });

    // Built-in sentiment colormap: `sentiment: 'fieldName'` or { field, domain: [lo, hi] }.
    // Auto-wires colorFunc (leaf cells) + groupColors (regions) from the diverging
    // pastel palette so a rating/sentiment field looks good with no extra code.
    // Explicit colorFunc / groupColors always take precedence.
    if (this.params.sentiment) {
      const sc = VoronoiBubbleHelpers.buildSentimentColoring(
        this.data,
        this.params.sentiment
      );
      if (!this.params.colorFunc) this.params.colorFunc = sc.colorFunc;
      if (!this.params.groupColors || this.params.groupColors.length === 0)
        this.params.groupColors = sc.groupColors;
    }

    // When colorFunc drives leaf colors but the user gave no palette/groupColors,
    // default the depth-1 (group) palette to a single neutral color so region
    // outlines and labels don't show clashing rainbow colors. Override with
    // `colors` / `groupColors` to restore per-region coloring.
    if (
      this.params.colorFunc &&
      !("colors" in normalizedOptions) &&
      (!this.params.groupColors || this.params.groupColors.length === 0)
    ) {
      this.params.colors = ["#444"];
    }

    this._setupSVG();
    this._prepareData();
    this._setupGroups();
    this._drawTitleAndCaption();
    this._createRegionColorScale();
    this._computeLayout();
    this._drawCells();
    this._drawCellImages();
    this._drawLabels();
    this._buildLabelCache();
    this._setupCellInteraction();
    this._applyHoverVisualMode();
    this._applyPostEffects();
    this._applyLabelMode();
    this._setupZoom();

    return this.svg.node();
  }

  /**
   * Update chart (data only)
   * @param {Object[]} newData - New data
   * @returns {SVGSVGElement}
   */
  update(newData) {
    return this.render(newData, this.params);
  }

  // === 1. Initial Setup Methods ===

  _setupSVG() {
    // Drawing coordinate space = params.width × viewBox height (the viewBox).
    // Display size is left to CSS: fills the container width, scales down on
    // mobile, and never grows past the native drawing size on large screens.
    // No JS scale math — the browser scales fonts/strokes/cells via the viewBox.

    // When there's a title, add ~1 line of breathing room above it: grow both
    // margin.top and the viewBox height by the same amount. The chart body and
    // the bottom margin stay the same size — everything just shifts down, so
    // the title gets headroom and nothing is clipped at the bottom.
    const hasTitle = !!(this.params.title && String(this.params.title).trim());
    const titleSpace = hasTitle ? 48 : 0;
    this.margin.top = 50 + titleSpace;
    const vbHeight = this.params.height + titleSpace;

    this.svg = d3
      .create("svg")
      .attr("class", "vb-chart")
      .attr("viewBox", `0 0 ${this.params.width} ${vbHeight}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("max-width", `${this.params.width}px`);

    // Inject bubble styles (includes :hover rules)
    this.svg.append("style").text(getBubbleStyles());

    this.svg
      .append("rect")
      .attr("width", "100%")
      .attr("height", "100%")
      .style("fill", "transparent");

    const innerWidth = this.params.width - this.margin.left - this.margin.right;
    const innerHeight = vbHeight - this.margin.top - this.margin.bottom;
    this.width = innerWidth * Math.sqrt(this.params.pieSize);
    this.height = this.width * (innerHeight / innerWidth);
  }

  _prepareData() {
    // Use external nestingForVoronoi function
    const nested = nestingForVoronoi(
      this.data,
      "subgroup",
      "item"
    );

    this.hierarchy = d3.hierarchy(nested, (d) => d.values).sum((d) => d.size);

    // Font scales are tuned for the 1200x900 reference canvas. Carry a
    // canvas-relative factor on the hierarchy so every font helper shrinks
    // text together with the cells on smaller/larger canvases.
    this.hierarchy.fontK =
      Math.sqrt((this.params.width * this.params.height) / (1200 * 900)) *
      (this.params.fontScale ?? 1);

    this.totalValue = this.hierarchy.value;
  }

  _setupGroups() {
    // outer wrapper: applies static margin offset (never touched by zoom)
    this.chartGroup = this.svg
      .append("g")
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // inner wrapper: applies dynamic zoom transform
    this.zoomGroup = this.chartGroup.append("g").attr("class", "vb-zoom-layer");

    this.voronoiGroup = this.zoomGroup.append("g").attr("class", "vb-cells");
    this.labelsGroup = this.zoomGroup.append("g").attr("class", "vb-item-labels");
    this.popLabelsGroup = this.zoomGroup.append("g").attr("class", "vb-item-values");
    this.bigLabelsGroup = this.zoomGroup.append("g").attr("class", "vb-subgroup-labels");
    this.regionLabelsGroup = this.zoomGroup
      .append("g")
      .attr("class", "vb-group-labels");
  }

  _drawTitleAndCaption() {
    const title = this.svg
      .append("g")
      .append("text")
      .attr("class", "vb-title")
      .attr("text-anchor", "middle")
      .attr("font-size", "28")
      .attr("font-weight", "600")
      .attr(
        "transform",
        `translate(${this.margin.left + this.width / 2},${
          this.margin.top - 40
        })`
      )
      .html(this.params.title);

    // 제목이 버블 영역 폭의 90%를 넘으면 말줄임(…) 처리.
    // render() 시점의 SVG는 아직 DOM 밖이라(getComputedTextLength=0),
    // 측정하는 동안만 숨김 상태로 body에 임시 부착한다.
    const titleNode = title.node();
    if (titleNode?.getComputedTextLength && typeof document !== "undefined") {
      const svgNode = this.svg.node();
      const detached = !svgNode.isConnected;
      if (detached && document.body) {
        svgNode.style.position = "absolute";
        svgNode.style.visibility = "hidden";
        document.body.appendChild(svgNode);
      }
      const maxW = this.width * 0.9;
      if (titleNode.getComputedTextLength() > maxW) {
        let text = titleNode.textContent;
        while (text.length > 1 && titleNode.getComputedTextLength() > maxW) {
          text = text.slice(0, -1);
          titleNode.textContent = text + "…";
        }
      }
      if (detached && svgNode.isConnected) {
        svgNode.remove();
        svgNode.style.position = "";
        svgNode.style.visibility = "";
      }
    }

    this.svg
      .append("g")
      .append("text")
      .attr("class", "vb-caption")
      .attr("text-anchor", "middle")
      .attr("font-size", "15")
      .attr("font-weight", "400")
      .attr("fill", "#888")
      .attr(
        "transform",
        `translate(${this.margin.left + this.width / 2},${
          this.margin.top + this.height + 30
        })`
      )
      .html(this.params.caption);
  }

  _createRegionColorScale() {
    // Calculate total size per group
    const regionSizes = d3.rollup(
      this.data,
      (v) => d3.sum(v, (d) => parseFloat(d.size) || 1),
      (d) => d.group
    );

    // Sort groups by size (descending - largest first)
    const sortedRegions = Array.from(regionSizes.entries())
      .sort((a, b) => b[1] - a[1]) // b[1] - a[1]: descending order
      .map((d) => d[0]); // Extract group names

    const { groupColors, colors: paletteColors } = this.params;
    const customColorMap = new Map(groupColors.map((d) => [d.key, d.color]));

    let colorMapping = {};

    // Assign colors to sorted regions (largest region gets first color)
    sortedRegions.forEach((key, i) => {
      colorMapping[key] = paletteColors[i % paletteColors.length];
    });

    // Override with custom region colors if specified
    customColorMap.forEach((color, key) => {
      colorMapping[key] = color;
    });

    this.regionColor = d3
      .scaleOrdinal()
      .domain(Object.keys(colorMapping))
      .range(Object.values(colorMapping));
  }

  // === 2. Layout Calculation Methods ===

  _computeLayout() {
    const ellipse = d3.range(100).map((i) => {
      let x = Math.cos((i / 50) * Math.PI);
      const max = 0.92;
      if (x > max) x = max + (x - max) * 0.5;
      if (x < -max) x = -max + (x + max) * 0.5;
      x = x / 0.94;
      const y = Math.sin((i / 25) * Math.PI);
      return [
        (this.width * (1 + 0.99 * x)) / 2,
        (this.height * (1 + 0.99 * y)) / 2
      ];
    });

    const hasCustomPositions =
      Array.isArray(this.params.positions) &&
      this.params.positions.length > 0;

    if (hasCustomPositions || this.params.adaptiveIterations) {
      const mergedPositions = hasCustomPositions
        ? this._normalizePositions(this.params.positions)
        : [];
      const seed = d3.seedrandom(this.params.seedRandom);
      VoronoiBubbleHelpers.createCustomVoronoiAlgorithm(
        this,
        this.params.debug
      )
        .size([this.width, this.height])
        .clip(ellipse)
        .prng(seed)
        .adaptiveIterations(!!this.params.adaptiveIterations)
        .initialPositions(mergedPositions)(this.hierarchy);
    } else {
      // Default: single voronoi run with fixed seed
      const seed = d3.seedrandom(this.params.seedRandom);
      d3.voronoiTreemap().prng(seed).clip(ellipse)(this.hierarchy);
    }

    VoronoiBubbleHelpers.colorHierarchy(this, this.hierarchy);

    this.allNodes = this.hierarchy
      .descendants()
      .sort((a, b) => b.depth - a.depth)
      .map((d, i) => Object.assign({}, d, { id: i }));

    // Extract and provide cell colors if callback is provided
    if (this.params.getCellColors) {
      const cellColors = this._extractCellColors();
      this.params.getCellColors(cellColors);
    }
  }

  /**
   * Extract cell colors from hierarchy nodes
   * @returns {Array} Array of {group, groupColor, subgroup, color} objects sorted by group size
   * @private
   */
  _extractCellColors() {
    const cellColors = [];

    // Get depth 1 nodes sorted by value (size) descending for ordering
    const depth1Nodes = this.hierarchy.descendants()
      .filter(n => n.depth === 1)
      .sort((a, b) => b.value - a.value);

    // Create order map for sorting
    const regionOrder = new Map();
    depth1Nodes.forEach((node, index) => {
      regionOrder.set(node.data.key, index);
    });

    this.hierarchy.descendants().forEach(node => {
      // Process depth 2 nodes (subgroup level)
      if (node.depth === 2) {
        // depth 1 = group (parent), depth 2 = subgroup (current node)
        const depth1Node = node.parent; // depth 1 ancestor

        cellColors.push({
          group: depth1Node?.data?.key,
          groupColor: depth1Node?.color,
          subgroup: node.data.key,
          color: node.color
        });
      }
    });

    // Sort by group size (largest first, following colormap order)
    cellColors.sort((a, b) => {
      const orderA = regionOrder.get(a.group) ?? Infinity;
      const orderB = regionOrder.get(b.group) ?? Infinity;
      return orderA - orderB;
    });

    return cellColors;
  }

  _normalizePositions(positions) {
    const result = [];
    const hierarchyNodes = this.hierarchy.descendants();

    // Helper: Add jitter to duplicate positions to prevent voronoi algorithm failure
    const addJitterForDuplicates = (positions, jitterAmount = 0.02) => {
      const positionMap = new Map(); // key: "x,y" -> count

      positions.forEach((pos) => {
        const key = `${pos.x.toFixed(6)},${pos.y.toFixed(6)}`;
        const count = positionMap.get(key) || 0;

        if (count > 0) {
          // Add jitter for duplicate positions (spread in a circle)
          const angle = (count * 2.4) % (2 * Math.PI); // Golden angle for even distribution
          const jitter = jitterAmount * Math.sqrt(count);
          pos.x = Math.max(0.05, Math.min(0.95, pos.x + jitter * Math.cos(angle)));
          pos.y = Math.max(0.05, Math.min(0.95, pos.y + jitter * Math.sin(angle)));
        }

        positionMap.set(key, count + 1);
      });

      return positions;
    };

    // depth 1: Normalize based on overall extent
    const depth1 = positions.filter((p) => p.depth === 1);
    if (depth1.length > 0) {
      const xExtent = d3.extent(depth1, (p) => p.x);
      const yExtent = d3.extent(depth1, (p) => p.y);

      const xScale =
        xExtent[0] === xExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(xExtent).range([0.15, 0.85]);
      const yScale =
        yExtent[0] === yExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(yExtent).range([0.15, 0.85]);

      const depth1Normalized = depth1.map((pos) => ({
        ...pos,
        x: xScale(pos.x),
        y: yScale(pos.y)
      }));

      // Add jitter for any duplicate positions
      addJitterForDuplicates(depth1Normalized);
      result.push(...depth1Normalized);
    }

    // depth 2, 3: normalize per depth, not per parent. Affinity/UMAP style
    // coordinates are usually computed over the whole depth set, so splitting
    // by parent would destroy the global semantic layout.
    [2, 3].forEach((depth) => {
      const depthPositions = positions.filter((p) => p.depth === depth);
      if (depthPositions.length === 0) return;

      const xExtent = d3.extent(depthPositions, (p) => p.x);
      const yExtent = d3.extent(depthPositions, (p) => p.y);

      const xScale =
        xExtent[0] === xExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(xExtent).range([0.15, 0.85]);
      const yScale =
        yExtent[0] === yExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(yExtent).range([0.15, 0.85]);

      const depthNormalized = depthPositions.map((pos) => ({
        ...pos,
        x: xScale(pos.x),
        y: yScale(pos.y)
      }));

      addJitterForDuplicates(depthNormalized);
      result.push(...depthNormalized);
    });

    return result;
  }

  // === 3. Visualization Element Drawing Methods ===

  _drawCells() {
    this.voronoiGroup
      .selectAll("path")
      .data(this.allNodes)
      .enter()
      .append("path")
      .attr("d", (d) => "M" + d.polygon.join("L") + "Z")
      .style("fill", (d) => d.color ?? d.parent.color)
      .style("--hl", (d) => {
        const base = d.color ?? d.parent?.color;
        const c = base ? d3.color(base) : null;
        return c ? c.darker(0.35).formatHex() : "#00000099";
      })
      .attr("class", "vb-cell")
      .attr("data-depth", (d) => d.depth)
      .attr("data-id", (d) => d.id)
      .style("fill-opacity", (d) => (d.depth === 3 ? 1 : 0))
      .attr("pointer-events", (d) => (d.depth === 3 ? "all" : "none"))
      .each(function (d) {
        if (d.depth === 3) d.cellNode = this;
      });
  }

  _setupCellInteraction() {
    const leaves = this.allNodes.filter((d) => d.depth === 3 && d.polygon?.length);
    if (!leaves.length) return;
    const visualHoverEnabled = leaves.length <= this.params.hoverVisualLimit;

    const restoreLabels = (d) => {
      if (!visualHoverEnabled || !d) return;
      const ratioLimit = this.params.ratioLimit;
      const groupLabel = this._subgroupLabelCache?.get(d.data.data.subgroup);
      const leafLabel = this._itemLabelCache?.get(d.data.data.item);
      if (groupLabel) {
        groupLabel.node().style.opacity = groupLabel._cachedRatio >= ratioLimit ? 1 : 0;
      }
      if (leafLabel) {
        leafLabel.node().style.opacity = leafLabel._cachedRatio >= ratioLimit ? 1 : 0;
      }
    };

    const revealLabels = (d) => {
      if (!visualHoverEnabled) return;
      const groupLabel = this._subgroupLabelCache?.get(d.data.data.subgroup);
      const leafLabel = this._itemLabelCache?.get(d.data.data.item);
      if (groupLabel) groupLabel.node().style.opacity = 1;
      if (leafLabel) leafLabel.node().style.opacity = 1;
    };

    const setHoveredCell = (next, event) => {
      if (next === this._hoveredCell) return;
      restoreLabels(this._hoveredCell);
      this._hoveredCell = next;

      if (!next) {
        this.params.onHover?.(null);
        return;
      }

      revealLabels(next);
      this.params.onHover?.({
        ...next.data.data,
        depth: next.depth,
        event,
        target: next.cellNode
      });
    };

    const cellSelection = this.voronoiGroup.selectAll('path.vb-cell[data-depth="3"]');
    if (this._hasExplicitOnHover) {
      cellSelection
        .on("mouseenter", (event, d) => setHoveredCell(d, event))
        .on("mouseleave", (event, d) => {
          if (this._hoveredCell === d) setHoveredCell(null, event);
        });
    }

    if (this._hasExplicitOnClick) {
      cellSelection.on("click", (event, d) => {
        const area = d3.select(event.currentTarget);
        const clicked = area.classed("vb-clicked");
        this.voronoiGroup.select(".vb-clicked").classed("vb-clicked", false);
        area.classed("vb-clicked", !clicked);
        this.params.onClick(clicked ? "" : { ...d.data, event, d, clickArea: area });
      });
    }

    cellSelection.style("cursor", this._hasExplicitOnClick || this._hasExplicitOnHover ? "pointer" : null);
  }

  _applyHoverVisualMode() {
    const leafCount = this.allNodes.filter((d) => d.depth === 3).length;
    this.svg.classed("vb-hover-enabled", leafCount <= this.params.hoverVisualLimit);
  }

  _drawCellImages() {
    const { cellImage } = this.params;
    if (!cellImage) return;

    const depth3 = this.allNodes.filter((d) => d.depth === 3);
    if (!depth3.length) return;

    let defs = this.svg.select("defs");
    if (defs.empty()) defs = this.svg.insert("defs", ":first-child");

    depth3.forEach((d) => {
      const imgOpts = cellImage(d.data.data ?? d.data);
      if (!imgOpts || !imgOpts.url) return;

      const { url, mode = "fill", opacity = 1, colorMode = "original" } = imgOpts;
      const id = `vb-cell-img-${d.id}`.replace(/[^a-zA-Z0-9-_]/g, "_");
      const polygon = d.polygon;
      const xs = polygon.map((p) => p[0]);
      const ys = polygon.map((p) => p[1]);
      const bx = Math.min(...xs), by = Math.min(...ys);
      const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;

      const clipId = `${id}-clip`;
      const clip = defs.append("clipPath").attr("id", clipId);
      clip.append("path").attr("d", "M" + polygon.join("L") + "Z");

      let filterAttr = null;
      if (colorMode === "tint") {
        const filterId = `${id}-tint`;
        const filter = defs.append("filter")
          .attr("id", filterId)
          .attr("x", "0%").attr("y", "0%")
          .attr("width", "100%").attr("height", "100%")
          .attr("color-interpolation-filters", "sRGB");
        filter.append("feColorMatrix").attr("type", "saturate").attr("values", "0").attr("result", "gray");
        filter.append("feFlood").attr("flood-color", d.color).attr("flood-opacity", "1").attr("result", "color");
        filter.append("feBlend").attr("in", "color").attr("in2", "gray").attr("mode", "multiply").attr("result", "tinted");
        filter.append("feComposite").attr("in", "tinted").attr("in2", "SourceGraphic").attr("operator", "in");
        filterAttr = `url(#${filterId})`;
      }

      let imgX = bx, imgY = by, imgW = bw, imgH = bh;
      if (mode === "fit") {
        const side = Math.min(bw, bh) * 0.8;
        imgX = bx + (bw - side) / 2;
        imgY = by + (bh - side) / 2;
        imgW = side; imgH = side;
      }

      const imgEl = this.voronoiGroup.append("image")
        .attr("href", url)
        .attr("x", imgX).attr("y", imgY)
        .attr("width", imgW).attr("height", imgH)
        .attr("preserveAspectRatio", mode === "fit" ? "xMidYMid meet" : "xMidYMid slice")
        .attr("clip-path", `url(#${clipId})`)
        .attr("opacity", opacity)
        .attr("pointer-events", "none");

      if (filterAttr) imgEl.attr("filter", filterAttr);
    });
  }

  _drawLabels() {
    this._drawRegionLabels();
    this._drawBigClusterLabels();
    this._drawSectorLabels();
    this._drawPopLabels();
  }

  _buildLabelCache() {
    // Build lookup maps for fast label access during hover events
    this._subgroupLabelCache = new Map();
    this._itemLabelCache = new Map();

    // Cache subgroup labels with their ratio values
    this.bigLabelsGroup.selectAll("[data-subgroup]").nodes().forEach((node) => {
      const key = node.getAttribute("data-subgroup");
      if (key) {
        const selection = d3.select(node);
        selection._cachedRatio = parseFloat(node.getAttribute("data-ratio")) || 0;
        this._subgroupLabelCache.set(key, selection);
      }
    });

    // Cache item labels with their ratio values
    this.labelsGroup.selectAll("[data-item]").nodes().forEach((node) => {
      const key = node.getAttribute("data-item");
      if (key) {
        const selection = d3.select(node);
        selection._cachedRatio = parseFloat(node.getAttribute("data-ratio")) || 0;
        this._itemLabelCache.set(key, selection);
      }
    });
  }

  _drawRegionLabels() {
    const { showGroupLabel, renderGroupLabel } = this.params;
    const self = this;

    const regionNodes = this.allNodes.filter((d) => d.depth === 1);

    // If custom renderer exists, use foreignObject
    if (renderGroupLabel) {
      this.regionLabelsGroup
        .selectAll("foreignObject")
        .data(regionNodes)
        .enter()
        .append("foreignObject")
        .attr("class", "vb-group-label-html")
        .attr("data-group", (d) => d.data.key)
        .attr("width", (d) => {
          const bounds = VoronoiBubbleHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return width * 0.6;
        })
        .attr("height", (d) =>
          VoronoiBubbleHelpers.estimateLabelHeight(this, d, 1.3)
        )
        .attr("x", (d) => {
          if (!d.polygon?.site) return 0;
          const bounds = VoronoiBubbleHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return d.polygon.site.x - (width * 0.6) / 2;
        })
        .attr(
          "y",
          (d) => {
            if (!d.polygon?.site) return 0;
            return d.polygon.site.y -
              VoronoiBubbleHelpers.estimateLabelHeight(this, d, 1.3) * 0.4 +
              VoronoiBubbleHelpers.getLabelHeightOffset(this, d);
          }
        )
        .style("opacity", showGroupLabel || this.params.showPercent ? 1 : 0)
        .style("pointer-events", "none")
        .style("overflow", "visible")
        .append("xhtml:div")
        .style("width", "100%")
        .style("height", "100%")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .html((d) => {
          const defaultHtml = VoronoiBubbleHelpers.multiline(d.data.key);
          const context = VoronoiBubbleHelpers.createLabelContext(this, d, 1);
          return renderGroupLabel(d, defaultHtml, context);
        });
    } else {
      // Default text rendering
      this.regionLabelsGroup
        .selectAll("text")
        .data(regionNodes)
        .enter()
        .append("text")
        .attr("class", "vb-group-label")
        .attr("text-anchor", "start")
        .attr("ratio", (d) => d.value / d.parent.value)
        .style(
          "font-size",
          (d) =>
            VoronoiBubbleHelpers.fontScale(this.hierarchy, d) * this.params.groupLabelScale + "em"
        )
        .style("fill-opacity", showGroupLabel || this.params.showPercent ? 1 : 0)
        .style("stroke-opacity", showGroupLabel || this.params.showPercent ? 0.85 : 0)
        .style(
          "stroke",
          (d) => `${VoronoiBubbleHelpers.getHSLColor(d.color, 0, -0.05, -0.2)}`
        )
        .attr("paint-order", "stroke")
        .attr(
          "transform",
          (d) => {
            if (!d.polygon?.site) return `translate(0,0)`;
            return `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiBubbleHelpers.getLabelHeightOffset(this, d)
            ]})`;
          }
        )
        .html((d) => {
          const label = VoronoiBubbleHelpers.multiline(d.data.key);
          if (!this.params.showPercent) return label;
          // Percent goes on an extra line inside the same <text>, so it always
          // shares the label's font. Positioned one line below the last row.
          const [, lines] = VoronoiBubbleHelpers.multiline(d.data.key, true);
          const pct = d3.format(".0%")(d.value / this.totalValue);
          return label +
            `<tspan x="0" y="${-lines / 2 + lines + 1.1}em" text-anchor="middle">` +
            `<tspan font-size="76%">${pct}</tspan></tspan>`;
        });
    }
  }

  _drawBigClusterLabels() {
    const { ratioLimit, renderSubgroupLabel } = this.params;
    const self = this;

    const bigClusterNodes = this.allNodes.filter((d) => d.depth === 2);

    // If custom renderer exists, use foreignObject
    if (renderSubgroupLabel) {
      this.bigLabelsGroup
        .selectAll("foreignObject")
        .data(bigClusterNodes)
        .enter()
        .append("foreignObject")
        .attr("class", "vb-subgroup-label-html")
        .attr("data-subgroup", (d) => d.data.key)
        .attr("data-value", (d) => d.value)
        .attr("data-ratio", (d) => d.value / this.totalValue)

        .attr("width", (d) => {
          const bounds = VoronoiBubbleHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return width * 0.6;
        })
        .attr("height", (d) =>
          VoronoiBubbleHelpers.estimateLabelHeight(this, d, 1.2)
        )
        .attr("x", (d) => {
          if (!d.polygon?.site) return 0;
          const bounds = VoronoiBubbleHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return d.polygon.site.x - (width * 0.6) / 2;
        })
        .attr(
          "y",
          (d) => {
            if (!d.polygon?.site) return 0;
            return d.polygon.site.y -
              VoronoiBubbleHelpers.estimateLabelHeight(this, d, 1.2) * 0.45 +
              VoronoiBubbleHelpers.getLabelHeightOffset(this, d);
          }
        )
        .attr("opacity", (d) =>
          d.value / this.totalValue >= ratioLimit ? 1 : 0
        )
        .style("pointer-events", "none")
        .style("overflow", "visible")
        .append("xhtml:div")
        .style("width", "100%")
        .style("height", "100%")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .html((d) => {
          const fontEm = VoronoiBubbleHelpers.fontScale(this.hierarchy, d) * this.params.subgroupLabelScale;
          const defaultHtml = VoronoiBubbleHelpers.phraseByCell(d.data.key, this.hierarchy, d, fontEm);
          const context = VoronoiBubbleHelpers.createLabelContext(this, d, 2);
          return renderSubgroupLabel(d, defaultHtml, context);
        });
    } else {
      // Default text rendering
      this.bigLabelsGroup
        .selectAll("text")
        .data(bigClusterNodes)
        .enter()
        .append("text")
        .attr("class", "vb-subgroup-label")
        .attr("data-subgroup", (d) => d.data.key)
        .attr("text-anchor", "start")
        .attr("data-value", (d) => d.value)
        .attr("data-ratio", (d) => d.value / this.totalValue)
        .style(
          "font-size",
          (d) => VoronoiBubbleHelpers.fontScale(this.hierarchy, d) * this.params.subgroupLabelScale + "em"
        )
        .attr("paint-order", "stroke")
        .style("fill", (d) =>
          VoronoiBubbleHelpers.colorVar2(d.parent.color, 0, 0.2, -0.2)
        )
        .attr(
          "transform",
          (d) => {
            if (!d.polygon?.site) return `translate(0,0)`;
            return `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiBubbleHelpers.getLabelHeightOffset(this, d)
            ]})`;
          }
        )
        .html((d) => {
          const fontEm = VoronoiBubbleHelpers.fontScale(this.hierarchy, d) * this.params.subgroupLabelScale;
          return VoronoiBubbleHelpers.phraseByCell(d.data.key, this.hierarchy, d, fontEm);
        })
        .attr("opacity", (d) =>
          d.value / this.totalValue >= ratioLimit ? 1 : 0
        );
    }

    // onSubgroupLabelHover: when set, depth-2 (subgroup) labels receive hover
    // events so a consumer can show a description. Labels are normally
    // pointer-events:none so clicks fall through to the cell behind them; we
    // preserve that by forwarding label clicks to the underlying cell (the
    // label itself only reacts to hover).
    const { onSubgroupLabelHover } = this.params;
    if (onSubgroupLabelHover) {
      const payloadOf = (d, e, node) => ({
        ...(d.data?.data || {}),
        subgroup: d.data.key,
        key: d.data.key,
        depth: d.depth,
        event: e,
        target: node,
      });
      this.bigLabelsGroup
        .selectAll("foreignObject.vb-subgroup-label-html, text.vb-subgroup-label")
        .style("pointer-events", "all")
        .style("cursor", "default")
        .on("mouseenter", function (e, d) { onSubgroupLabelHover(payloadOf(d, e, this)); })
        .on("mouseleave", function () { onSubgroupLabelHover(null); })
        .on("click", function (e) {
          // Hover-only label: forward the click to the cell behind it.
          this.style.pointerEvents = "none";
          const under = document.elementFromPoint(e.clientX, e.clientY);
          this.style.pointerEvents = "all";
          if (under && under !== this) {
            under.dispatchEvent(new MouseEvent("click", {
              bubbles: true, cancelable: true, view: window,
              clientX: e.clientX, clientY: e.clientY,
            }));
          }
        });
    }
  }

  _drawSectorLabels() {
    const { underLabel, ratioLimit } = this.params;

    this.labelsGroup
      .selectAll("text")
      .data(this.allNodes.filter((d) => d.depth === 3))
      .enter()
      .append("text")
      .attr("class", "vb-item-label")
      .attr("data-id", (d) => d.id)
      .attr("data-item", (d) => d.data.key)
      .attr("data-full-text", (d) => d.data.key)
      .attr("data-font-em", (d) => VoronoiBubbleHelpers.fontScale2(this.hierarchy, d))
      .attr("data-cell-w", (d) => {
        const xs = d.polygon.map(p => p[0]);
        return Math.max(...xs) - Math.min(...xs);
      })
      .attr("data-cell-h", (d) => {
        const ys = d.polygon.map(p => p[1]);
        return Math.max(...ys) - Math.min(...ys);
      })
      .attr("data-value", (d) => d.value)
      .attr("data-ratio", (d) => d.value / this.totalValue)
      .attr("text-anchor", "start")
      .style(
        "font-size",
        (d) => VoronoiBubbleHelpers.fontScale2(this.hierarchy, d) + "em"
      )
      .style("fill", (d) =>
        VoronoiBubbleHelpers.getHSLColor(d.color, 0, -0.1, -0.3)
      )
      .attr("transform", (d) => {
        if (!d.polygon?.site) return `translate(0,0)`;
        return underLabel
          ? `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiBubbleHelpers.fontScale1(
                  this.hierarchy,
                  d.data.data.subgroup,
                  d.parent.value
                ) *
                  8 *
                  (VoronoiBubbleHelpers.multiline(
                    d.data.data.subgroup,
                    true
                  )[1] +
                    0.5)
            ]})`
          : `translate(${VoronoiBubbleHelpers.getLabelPos(this, d)})`;
      })
      .html((d) => {
        const { text, charsPerLine } = VoronoiBubbleHelpers.truncateByCell(d.data.key, this.hierarchy, d);
        return VoronoiBubbleHelpers.multiline(text, false, charsPerLine, 1.4);
      })
      .attr("opacity", (d) => (d.value / this.totalValue > ratioLimit ? 1 : 0));
  }

  _drawPopLabels() {
    const { sizeLimit } = this.params;

    this.popLabelsGroup
      .selectAll("text")
      .data(this.allNodes.filter((d) => d.depth === 3))
      .enter()
      .append("text")
      .attr("class", "vb-item-value")
      .attr("data-id", (d) => d.id)
      .attr("text-anchor", "middle")
      .style(
        "font-size",
        (d) => VoronoiBubbleHelpers.fontScale2(this.hierarchy, d) * 0.8 + "em"
      )
      .attr(
        "data-item",
        (d) => d.data.data.item ?? d.data.data.subgroup
      )
      .attr(
        "transform",
        (d) => {
          if (!d.polygon?.site) return `translate(0,0)`;
          return `translate(${[
            d.polygon.site.x,
            d.polygon.site.y + VoronoiBubbleHelpers.varFontScale(this, d)
          ]})`;
        }
      )
      .text((d) => VoronoiBubbleHelpers.bigFormat(d.data.values[0].size))
      .attr("opacity", (d) => (d.value > sizeLimit ? 1 : 0));
  }

  // === 4. Post-processing and Effects Methods ===

  _applyPostEffects() {
    const { showGroupLabel, pebbleRound, pebbleWidth } = this.params;

    if (showGroupLabel) {
      this.labelAdjuster.adjust(this.svg.node(), { verticalSpacing: 0 });
    }

    this.pebbleRenderer.render(
      this.svg.node(),
      pebbleRound,
      pebbleWidth,
      VoronoiBubbleHelpers.colorVar.bind(VoronoiBubbleHelpers)
    );
  }

  _applyLabelMode(opacity) {
    const { labelMode } = this.params;
    const op = opacity !== undefined ? opacity
      : labelMode === 'show' ? 1
      : labelMode === 'faded' ? 0.6
      : 0;
    this.svg.selectAll('.vb-item-labels tspan').style('opacity', op);
    this.svg.selectAll('.vb-cell[data-depth="3"]').style('stroke-opacity', op);
  }

  _setupZoom() {
    const svg = this.svg;
    const zoomGroup = this.zoomGroup;
    const baseFontPx = 16;
    let lastK = 1;

    const zoom = d3.zoom()
      .scaleExtent([1, 12])
      // Zoom/pan only while the Option/Alt key is held. Touch events (mobile)
      // carry no altKey, so they never pass — this also disables zoom on mobile.
      // dblclick-to-reset is a separate custom listener below, unaffected by this.
      .filter((event) => event.altKey && !event.button)
      .constrain((transform) => {
        const { k } = transform;
        if (k <= 1) return d3.zoomIdentity;
        // Allow panning so chart edges can reach viewport edges
        // (margin offsets are absorbed by chartGroup's static transform)
        const xMin = Math.min(0, this.params.width - this.margin.left - k * this.width);
        const yMin = Math.min(0, this.params.height - this.margin.top - k * this.height);
        const x = Math.max(xMin, Math.min(0, transform.x));
        const y = Math.max(yMin, Math.min(0, transform.y));
        return new transform.constructor(k, x, y);
      })
      .on('zoom', (event) => {
        const { x, y, k } = event.transform;
        zoomGroup.attr('transform', `translate(${x},${y}) scale(${k})`);

        // phase 1 (k≤growMax): text grows with zoom
        // phase 2 (k>growMax): text held at growMax screen size, char count grows instead
        const growMax = 2;
        const textScale = k <= growMax ? 1 : growMax / k;

        // labelMode: fade in labels/borders as zoom increases
        const modeOpacity = this.params.labelMode === 'show' ? 1
          : this.params.labelMode === 'faded' ? 0.6
          : 0;
        const zoomOpacity = modeOpacity + (1 - modeOpacity) * Math.min(1, k - 1);
        svg.selectAll('.vb-item-labels tspan').style('opacity', zoomOpacity);
        svg.selectAll('.vb-cell[data-depth="3"]').style('stroke-opacity', zoomOpacity);

        // keep stroke visually thin as zoom increases
        svg.selectAll('.vb-cell[data-depth="3"]').style('stroke-width', `${0.5 / k}px`);
        svg.selectAll('.vb-cell[data-depth="2"]').style('stroke-width', `${0.7 / k}px`);
        svg.selectAll('.vb-cell[data-depth="1"]').style('stroke-width', `${1.5 / k}px`);
        svg.selectAll('text.vb-item-label, text.vb-subgroup-label, .vb-group-labels text, .vb-subgroup-labels text')
          .attr('transform', function() {
            const el = d3.select(this);
            const orig = el.attr('data-orig-transform') || el.attr('transform') || 'translate(0,0)';
            if (!el.attr('data-orig-transform')) el.attr('data-orig-transform', orig);
            return `${orig} scale(${textScale})`;
          });

        svg.selectAll('foreignObject.vb-group-label-html, foreignObject.vb-subgroup-label-html')
          .each(function() {
            const fo = d3.select(this);
            if (!fo.attr('data-orig-x')) {
              fo.attr('data-orig-x', fo.attr('x') || '0')
                .attr('data-orig-y', fo.attr('y') || '0')
                .attr('data-orig-w', fo.attr('width') || '0')
                .attr('data-orig-h', fo.attr('height') || '0');
            }
            const cx = parseFloat(fo.attr('data-orig-x')) + parseFloat(fo.attr('data-orig-w')) / 2;
            const cy = parseFloat(fo.attr('data-orig-y')) + parseFloat(fo.attr('data-orig-h')) / 2;
            fo.attr('transform', `translate(${cx * (1 - textScale)},${cy * (1 - textScale)}) scale(${textScale})`);
          });

        // re-truncate text elements when zoom level changes by >20%
        if (Math.abs(k - lastK) / lastK > 0.2) {
          lastK = k;
          svg.selectAll('text.vb-item-label[data-full-text]').each(function() {
            const el = d3.select(this);
            const fullText = el.attr('data-full-text');
            const cellW = parseFloat(el.attr('data-cell-w') || 0);
            const cellH = parseFloat(el.attr('data-cell-h') || 0);
            const fontEm = parseFloat(el.attr('data-font-em') || 0.7);
            const fontPx = fontEm * baseFontPx;
            const isLatin = !/[^\x00-\x7F]/.test(fullText);
            const charPx = isLatin ? fontPx * 0.55 : fontPx;
            // text-to-cell ratio scales by 1/textScale; identical to base in phase 1
            const charsPerLine = Math.max(1, Math.floor((cellW * 0.6) / (charPx * textScale)));
            const linesFit = Math.max(1, Math.floor(cellH / (fontPx * 1.4 * textScale)));
            const limit = Math.max(5, Math.min(fullText.length, charsPerLine * linesFit));
            const truncated = fullText.length <= limit ? fullText : fullText.slice(0, limit) + '…';
            el.html(VoronoiBubbleHelpers.multiline(truncated, false, charsPerLine, 1.4));
          });
        }
      });

    svg.call(zoom);

    // snap to origin when zoomed out to minimum
    zoom.on('end', (event) => {
      if (event.transform.k <= 1) {
        svg.transition().duration(120).call(zoom.transform, d3.zoomIdentity);
      }
    });

    // double-click to reset
    svg.on('dblclick.zoom', () => {
      svg.transition().duration(200).call(zoom.transform, d3.zoomIdentity);
    });
  }
}

export default VoronoiBubble;
